import { LanguagesEnum } from '@/api/enums';
import { handleAxiosError, notEqual } from '@/api/helpers';
import { AppBreadCrumbTemplate } from '@/app/AppBreadCrumbTemplate';
import { Account } from '@/client/accounts';
import { AccountDomainStatus } from '@/client/accounts/types/account-domain.type';
import { ScopeMode } from '@/client/custom-roles';
import { Group } from '@/client/groups';
import { USER_ALREADY_EXISTS_ERROR } from '@/client/helpers';
import { HubspotProperty, HubspotValue } from '@/client/hubspot/types';
import { Role } from '@/client/roles';
import { Actions, Subjects, SystemRoles, UserFormState } from '@/client/users';
import { UserOrigin } from '@/client/users/types/origins.enum';
import { RedirectPaths, RedirectPathsEnum } from '@/common/constants';
import { TranslationFunctionType } from '@/common/types';
import { UserForm } from '@/components/users/forms';
import {
  useAccountDomains,
  useCustomRoles,
  useGroupsPartialRequest,
  useRestoreUser,
  useRoles,
  useSaveUser,
  useUpdateUser,
} from '@/hooks/query';
import { useAppSelector } from '@/hooks/store';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { usePermission } from '@/hooks/usePermission';
import { useToast } from '@/hooks/useToast';
import { selectCurrentAccount } from '@/store/features/account';
import { AppBreadCrumb } from '@/ui/breadcrumb';
import { FlexContainer } from '@/ui/styled-ui';
import {
  asCustomRoleOption,
  resolveRoleAssignment,
} from '@/utils/custom-roles';
import {
  getIsActivePhishingStatus,
  queryStateConverter,
} from '@/utils/helpers';
import { hubspotTrack } from '@/utils/hubspot/hubspot.helper';
import { PostHogFeatureFlags } from '@/utils/posthog';
import { AxiosError } from 'axios';
import { FormikValues } from 'formik';
import { MenuItem } from 'primereact/menuitem';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { LoadingPage } from '../LoadingPage';

const getBreadcrumbs = (
  account: Account,
  t: TranslationFunctionType,
): MenuItem[] => [
  {
    label: account?.name,
    url: !account?.isSystem
      ? RedirectPaths[RedirectPathsEnum.ACCOUNT](account?.id)
      : RedirectPaths[RedirectPathsEnum.EDIT_ACCOUNT](account?.id),
    template: AppBreadCrumbTemplate,
  },
  {
    label: t('users'),
    url: RedirectPaths[RedirectPathsEnum.USERS](),
    template: AppBreadCrumbTemplate,
  },
  {
    label: t('user.new'),
    url: RedirectPaths[RedirectPathsEnum.USERS_CREATE](),
    template: AppBreadCrumbTemplate,
  },
];

export const CreateUserPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const account = useAppSelector(selectCurrentAccount);
  const { roles } = useRoles({
    take: 20,
  });

  const { can } = usePermission();
  const { canUseFeature, accountCanUseFeature } = useFeatureFlag();
  const isDomainVerificationEnabled = canUseFeature(
    PostHogFeatureFlags.USER_DOMAIN_VERIFICATION,
  );

  const canAssignRole = can(Actions.READ, Subjects.ROLES);

  const customRolesEnabled =
    canAssignRole &&
    accountCanUseFeature(
      PostHogFeatureFlags.CUSTOM_ROLES,
      account?.featureFlagAbility ?? [],
    );
  const { customRoles } = useCustomRoles(
    customRolesEnabled ? (account?.id as string) : '',
  );

  const toRoleAccessSettings = () =>
    navigate(RedirectPaths[RedirectPathsEnum.ACCOUNT_ROLES_ACCESS]());

  const learnerRoleId = roles?.result?.find(
    ({ code }) => code === SystemRoles.LEARNER,
  )?.id;
  const { domains, isLoading: isDomainsLoading } = useAccountDomains(
    isDomainVerificationEnabled ? account?.id : undefined,
  );

  const { groups, isLoading: isGroupsLoading } = useGroupsPartialRequest({
    accountId: account?.id,
    sort: ['name,asc'],
    filters: [notEqual('isForPhishing', true)],
  });

  const [initialValues, setInitialValues] = useState<UserFormState>({
    firstName: '',
    lastName: '',
    username: '',
    email: '',
    password: undefined,
    role: undefined,
    scopeGroups: [],
    active: true,
    branch: undefined,
    language: account?.defaultLanguage ?? LanguagesEnum.DK,
    origin: undefined,
    assignedAccounts: undefined,
    expiresOn: undefined,
    reactivatesOn: undefined,
    isTemporary: false,
  });

  useEffect(() => {
    if (roles?.count) {
      setInitialValues({
        ...initialValues,
        role: roles?.result?.find((role) => role.code === SystemRoles.LEARNER),
      });
    }
  }, [roles?.result]);

  const createUser = useSaveUser();
  const updateUser = useUpdateUser();
  const restoreUser = useRestoreUser();

  const handleUserAction = async (data: FormikValues) => {
    await createUser.create({
      firstName: data.firstName,
      lastName: data.lastName,
      username: data.username,
      active: data.active,
      accountId: (account as Account)?.id,
      password: data.password,
      email: data.email,
      ...(!canAssignRole
        ? {}
        : customRolesEnabled
        ? {
            ...resolveRoleAssignment(data.role, learnerRoleId),
            scopeGroups:
              asCustomRoleOption(data.role)?.scopeMode === ScopeMode.GROUPS
                ? data.scopeGroups?.map(({ id }: Group) => id) ?? []
                : [],
          }
        : { role: data.role.id }),
      language: data.language,
      branch: data.branch ? data.branch.id : null,
      groups: data.groups ? data.groups.map(({ id }: Group) => id) : null,
      origin: data.isManual ? UserOrigin.MANUAL : undefined,
      assignedAccounts: data.assignedAccounts
        ? data.assignedAccounts.map(({ id }: Account) => id)
        : null,
      expiresOn:
        data.isTemporary &&
        (data.role?.code === SystemRoles.ADMIN ||
          data.role?.code === SystemRoles.MULTIPLE_ACCOUNTS_ADMIN)
          ? data.expiresOn
          : null,
      reactivatesOn: data.reactivatesOn || null,
    });

    navigate(RedirectPaths[RedirectPathsEnum.USERS]());

    hubspotTrack(HubspotProperty.NEW_USER_CREATED, HubspotValue.YES);
  };

  const handleGoToUser = (userId: string) => {
    navigate(RedirectPaths[RedirectPathsEnum.USERS_EDIT](userId));
  };

  const handleReactivateUser = async (userId: string) => {
    try {
      await updateUser.update({
        userId,
        accountId: (account as Account)?.id,
        updates: { active: true },
      });

      toast?.success(t('toast.success'), t('user.reactivated'));
      navigate(RedirectPaths[RedirectPathsEnum.USERS_EDIT](userId));
    } catch (e) {
      handleAxiosError(e as Error | AxiosError, toast);
    }
  };

  const handleRestoreConflictUser = async (userId: string, name: string) => {
    try {
      await restoreUser.restore({
        userId,
        accountId: (account as Account)?.id,
      });

      toast?.success(t('toast.success'), t('user.restoredNamed', { name }));
      navigate(RedirectPaths[RedirectPathsEnum.USERS_EDIT](userId));
    } catch (e) {
      handleAxiosError(e as Error | AxiosError, toast);
    }
  };

  const handleSubmit = async (data: FormikValues) => {
    try {
      await handleUserAction(data);

      toast?.success(t('toast.success'), t('user.created'));

      if (isDomainVerificationEnabled && !isDomainsLoading) {
        const emailDomain = data.email?.split('@')[1]?.toLowerCase();
        const domainMatch = domains?.find(
          (d) => d.domain?.toLowerCase() === emailDomain.toLowerCase(),
        );

        const isDomainVerified = domainMatch
          ? domainMatch.status === AccountDomainStatus.VERIFIED
          : false;

        if (!isDomainVerified && getIsActivePhishingStatus(account)) {
          toast?.action({
            severity: 'warn',
            summary: '',
            detail: t('user.domain.notVerifiedPhishingWarning'),
            actionLabel: t('user.domain.verifyNow'),
            onAction: () =>
              navigate(
                account?.isSystem
                  ? RedirectPaths[
                      RedirectPathsEnum.ACCOUNTS_EMAILS_AND_DELIVERY_DOMAINS
                    ](account.id)
                  : RedirectPaths[
                      RedirectPathsEnum.ACCOUNT_EMAILS_AND_DELIVERY_DOMAINS
                    ](),
              ),
          });
        }
      }
    } catch (e: any) {
      // Temporary solution
      if (e.message === USER_ALREADY_EXISTS_ERROR) {
        toast?.success(t('toast.success'), t('user.checkIfAvailable'));
        navigate(RedirectPaths[RedirectPathsEnum.USERS]());
        return;
      }

      handleAxiosError(e as Error | AxiosError, toast);
    }
  };

  if (isGroupsLoading) {
    return <LoadingPage message={t('generic.loading')} />;
  }

  return (
    <>
      <AppBreadCrumb model={getBreadcrumbs(account as Account, t)} />
      <h1>{t('user.new')}</h1>
      <FlexContainer justify="flex-start">
        <UserForm
          initialValues={initialValues}
          onSubmit={handleSubmit}
          onGoToUser={handleGoToUser}
          onReactivateUser={handleReactivateUser}
          onRestoreConflictUser={handleRestoreConflictUser}
          restoreConflictState={queryStateConverter(restoreUser)}
          state={queryStateConverter(createUser)}
          reactivateState={queryStateConverter(updateUser)}
          isCreate
          roles={roles?.result as Role[]}
          customRoles={customRoles}
          onChangeAccessSettings={toRoleAccessSettings}
          groups={groups as Group[]}
        />
      </FlexContainer>
    </>
  );
};
